import React, { useState, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import { FaUserCog } from 'react-icons/fa';
import Navigation from './Navigation';
import AdminFormLayout from './AdminFormLayout';
import { fetchActiveOutletModules } from './outletModuleAccess';

function KaryawanModulesForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;

    const [formData, setFormData] = useState({
        id_karyawan: '',
        modules: []
    });
    const [karyawanOptions, setKaryawanOptions] = useState([]);
    const [moduleOptions, setModuleOptions] = useState([]);
    const [moduleLoadError, setModuleLoadError] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        if (!token) {
            navigate('/loginadmin');
            return;
        }
        fetchKaryawanList(token);
    }, [navigate]);

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        if (isEditing && token && karyawanOptions.length > 0) {
            fetchKaryawanModuleDetails(token);
        }
    }, [id, karyawanOptions, isEditing]);

    useEffect(() => {
        const token = localStorage.getItem('sessionToken');
        let active = true;

        const syncAvailableModules = async () => {
            const selectedKaryawan = karyawanOptions.find(
                karyawan => karyawan.id_karyawan === formData.id_karyawan
            );

            if (!token || !selectedKaryawan) {
                if (active) {
                    setModuleLoadError('');
                    setModuleOptions([]);
                    setFormData(prev => ({
                        ...prev,
                        modules: [],
                    }));
                }
                return;
            }

            try {
                if (active) {
                    setModuleLoadError('');
                }
                const allowedModules = await fetchActiveOutletModules(token, selectedKaryawan.outlet_id);
                if (!active) {
                    return;
                }

                const allowedIdSet = new Set(allowedModules.map(module => String(module.ID)));
                setModuleOptions(allowedModules);
                setFormData(prev => ({
                    ...prev,
                    modules: prev.modules.filter(moduleId => allowedIdSet.has(String(moduleId))),
                }));
            } catch (err) {
                console.error('Failed to fetch outlet-specific modules:', err);
                if (active) {
                    setModuleLoadError(err.message || 'Failed to load active outlet modules.');
                    setModuleOptions([]);
                }
            }
        };

        syncAvailableModules();

        return () => {
            active = false;
        };
    }, [formData.id_karyawan, karyawanOptions]);

    const fetchKaryawanList = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}karyawanlist`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ limit: 100, start: 0 })
            });
            const data = await response.json();
            if (String(data.error) === "0" && data.results) {
                const options = data.results.map(karyawan => ({
                    ID: karyawan.id,
                    Name: `${karyawan.nama} (${karyawan.OutletName})`,
                    id_karyawan: karyawan.id.toString(),
                    outlet_id: String(karyawan.outlet || '')
                }));
                setKaryawanOptions(options);
            }
        } catch (err) {
            console.error('Failed to fetch karyawan list:', err);
        }
    };

    const fetchKaryawanModuleDetails = async (token) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}getkaryawanmodules`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify({ "id_karyawan":  parseInt(id, 10) })
            });
            
            const data = await response.json();
            console.log(data);
            if (String(data.error) === '0' && data.data) {
                setFormData({
                    id_karyawan: data.data.id_karyawan.toString(),
                    modules: data.data.modules.map(moduleId => moduleId.toString())
                });
            }
        } catch (err) {
            console.error('Failed to fetch karyawan module details:', err);
        }
    };

    const handleModuleChange = (moduleId) => {
        setFormData(prevState => {
            const currentModules = new Set(prevState.modules);
            if (currentModules.has(moduleId)) {
                currentModules.delete(moduleId);
            } else {
                currentModules.add(moduleId);
            }
            return { ...prevState, modules: Array.from(currentModules) };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const token = localStorage.getItem('sessionToken');
        const endpoint = isEditing ? 'updatemoduleskaryawan' : 'insertmoduleskaryawan';

        try {
            const payload = {
                id_karyawan: parseInt(formData.id_karyawan, 10),
                modules: formData.modules.map(Number)
            };

            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}${endpoint}`, {
                method: isEditing ? 'PUT' : 'POST',
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(payload)
            });

            const data = await response.json();
            if (data.error === 0) {
                Swal.fire({
                    icon: 'success',
                    title: 'Success',
                    text: `Karyawan modules ${isEditing ? 'updated' : 'created'} successfully!`
                }).then(() => navigate('/karyawanmodules'));
            } else {
                throw new Error(data.message || 'Operation failed');
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Error', text: err.message });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Navigation />
            <AdminFormLayout
                icon={FaUserCog}
                eyebrow="Access administration"
                title={isEditing ? 'Update Employee Module Assignment' : 'Create Employee Module Assignment'}
                description="Tentukan modul yang dapat diakses oleh karyawan tertentu sesuai dengan perannya dalam operasional harian."
                breadcrumbs={[
                    { label: 'Dashboard', to: '/dashboardadmin' },
                    { label: 'Employee Module Assignment', to: '/karyawanmodules' },
                    { label: isEditing ? 'Update Assignment' : 'Create Assignment' },
                ]}
                tipsTitle="Assignment Notes"
                tips={[
                    'Pilih karyawan yang sesuai sebelum menetapkan modul.',
                    'Gunakan kombinasi modul yang relevan dengan tugas dan tanggung jawab karyawan.',
                    'Perubahan akan langsung diperhitungkan pada hak akses operasional.',
                ]}
            >
                <Form onSubmit={handleSubmit}>
                    <div className="field-stack">
                        <Form.Label>Employee</Form.Label>
                        <Form.Select
                            value={formData.id_karyawan}
                            onChange={(e) => setFormData(prev => ({ ...prev, id_karyawan: e.target.value }))}
                            required
                        >
                            <option value="">Select employee</option>
                            {karyawanOptions.map(karyawan => (
                                <option key={karyawan.ID} value={karyawan.id_karyawan}>
                                    {karyawan.Name}
                                </option>
                            ))}
                        </Form.Select>
                    </div>

                    <div className="field-stack">
                        <Form.Label>Modules</Form.Label>
                        <div className="check-grid">
                            {moduleLoadError ? (
                                <p className="text-danger mb-0">{moduleLoadError}</p>
                            ) : moduleOptions.length > 0 ? (
                                moduleOptions.map(module => (
                                    <Form.Check
                                        key={module.ID}
                                        type="checkbox"
                                        label={module.Modulename}
                                        checked={formData.modules.includes(module.ID.toString())}
                                        onChange={() => handleModuleChange(module.ID.toString())}
                                    />
                                ))
                            ) : !formData.id_karyawan ? (
                                <p className="text-muted mb-0">Select an employee first to load the active module list.</p>
                            ) : (
                                <p className="text-muted mb-0">No active modules are available for the selected employee outlet.</p>
                            )}
                        </div>
                        <Form.Text>
                            Daftar ini otomatis mengikuti modul yang aktif pada outlet milik karyawan terpilih.
                        </Form.Text>
                    </div>

                    <div className="form-actions">
                        <Button type="submit" variant="primary" disabled={loading}>
                            {loading ? 'Processing...' : (isEditing ? 'Save Changes' : 'Save Assignment')}
                        </Button>
                        <Button variant="secondary" onClick={() => navigate('/karyawanmodules')}>
                            Cancel
                        </Button>
                    </div>
                </Form>
            </AdminFormLayout>
        </div>
    );
}

export default KaryawanModulesForm;
